import { SliceComponentProps } from "@prismicio/react";
import { Slice } from "@prismicio/types";
import React from "react";

import {
  LatestCapabilitiesSlice,
  PrismicCapabilityDataBodyCenteredHero,
  PrismicCapabilityDataBodyInstantQuoteHero,
} from "../../types/graphql";
import { IPageContext } from "../../types/types";
import { linkTo } from "../../utils/contentUtils";
import { IGetLatestCapabilitiesQueriesResults } from "../../utils/get-latest-capabilties-pages";
import { SharedSliceType } from "../shared-slice/shared-slice";
import {
  ImageContainer,
  Image,
  Headline,
  CapabilitiesList,
  ButtonWrapper,
  ResourceButton,
  SmallPostCardBase,
  Title,
  TextWrapper,
  SubjectLink,
  FallbackImage,
} from "./latest-capabiltites.styled";
import fallbackImage from "../../images/xometry-logo-big-ideas.jpeg";

export type ILatestCapabilities = Slice<SharedSliceType> &
  LatestCapabilitiesSlice;

const LatestCapabilities = ({
  slice,
  context,
}: SliceComponentProps<ILatestCapabilities, IPageContext>) => {
  const headline = slice.primary.headline;
  const buttonLink = slice.primary?.button_link;
  const buttonText = slice.primary?.button_text;
  const latestCapabilitiesPost = (context.latestCapabilities?.[slice.id] as any)
    ?.nodes as IGetLatestCapabilitiesQueriesResults[string];

  const getHero = (
    capability: IGetLatestCapabilitiesQueriesResults[string][number]
  ) => {
    return capability.data.body.find(
      (slice) =>
        "slice_type" in slice &&
        slice.slice_type.match(/instant_quote_hero|centered_hero/)
    );
  };

  const getCapabilityTitle = (
    capability: IGetLatestCapabilitiesQueriesResults[string][number]
  ) => {
    const hero = getHero(capability);
    if (hero && "slice_type" in hero && hero?.slice_type == "centered_hero") {
      return (hero as PrismicCapabilityDataBodyCenteredHero).primary.title;
    } else {
      return (hero as PrismicCapabilityDataBodyInstantQuoteHero).primary
        .capability_title?.text;
    }
  };

  const getCapabilityImage = (
    capability: IGetLatestCapabilitiesQueriesResults[string][number]
  ) => {
    const hero = getHero(capability);

    if (hero && "slice_type" in hero && hero?.slice_type == "centered_hero") {
      const image = (hero as PrismicCapabilityDataBodyCenteredHero).primary
        .image;
      return { gatsbyImageData: image?.gatsbyImageData, alt: image?.alt };
    } else {
      const image = (hero as PrismicCapabilityDataBodyInstantQuoteHero).primary
        .page_image;
      return { gatsbyImageData: image?.gatsbyImageData, alt: image?.alt };
    }
  };
  // Render nothing if queries fail.
  if (!latestCapabilitiesPost) return <></>;
  return (
    <section className="slice-section">
      <div className="container">
        {headline && <Headline>{headline}</Headline>}
        <CapabilitiesList>
          {latestCapabilitiesPost &&
            latestCapabilitiesPost.map((capability) => (
              <SmallPostCardBase key={capability.uid}>
                <ImageContainer>
                  {getCapabilityImage(capability).gatsbyImageData && (
                    <Image
                      alt={getCapabilityImage(capability).alt || ""}
                      image={getCapabilityImage(capability).gatsbyImageData}
                    />
                  )}
                  {!getCapabilityImage(capability).gatsbyImageData && (
                    <FallbackImage
                      src={fallbackImage}
                      alt="Xometry Capability"
                    />
                  )}
                </ImageContainer>
                <TextWrapper>
                  <SubjectLink to={linkTo(capability)}>
                    <Title>{getCapabilityTitle(capability)}</Title>
                  </SubjectLink>
                </TextWrapper>
              </SmallPostCardBase>
            ))}
        </CapabilitiesList>
        {buttonLink && buttonText && (
          <ButtonWrapper>
            <ResourceButton to={linkTo(buttonLink)}>
              {buttonText}
            </ResourceButton>
          </ButtonWrapper>
        )}
      </div>
    </section>
  );
};

export default LatestCapabilities;
